import React from 'react';
import { type CommunicationRecord } from '@entities';
import { Card, CardContent, Box, Typography, alpha, useTheme } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { format } from 'date-fns';
import { communicationChannelLabel } from '@shared/helpers/communication-channel';

export const CommunicationCard: React.FC<{
    record: CommunicationRecord;
    onClick: () => void;
}> = ({ record, onClick }) => {
    const theme = useTheme();
    const channelLabel = communicationChannelLabel(record.channel);
    const opportunityTitle = record.opportunity?.title?.trim();
    const typeLabel =
        record.type === "volunteer"
            ? "מתנדב/ת"
            : record.type === "aid-related"
              ? "מבקש/ת סיוע"
              : record.type === "opportunity"
                ? "משימה"
                : "";

    return (
        <Card
            className="communication-card"
            onClick={onClick}
            sx={{
                marginBottom: '0.5rem !important',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                minWidth: 'unset !important',
                borderRadius: 2,
                justifyContent: 'flex-start !important',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4],
                },
            }}
        >
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        color: 'text.secondary',
                        '& svg': {
                            fontSize: '1.2rem',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                        <AccessTimeIcon />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                            {format(new Date(record.createdAt), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                    </Box>
                    {channelLabel ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <Typography variant="body2">{channelLabel}</Typography>
                        </Box>
                    ) : null}
                    {typeLabel ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <PersonSearchIcon sx={{ mr: 0.3 }} />
                            <Typography variant="body2">{typeLabel}</Typography>
                        </Box>
                    ) : null}
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                        <PersonIcon />
                        <Typography variant="body2" sx={{ ml: 0.3 }}>
                            {record.manager?.name || 'לא זמין'}
                        </Typography>
                    </Box>
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.primary',
                        fontWeight: 'bold',
                        mb: 1,
                    }}
                >
                    {record.title || ''}
                </Typography>
                {opportunityTitle ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        משימה: {opportunityTitle}
                    </Typography>
                ) : null}
                <Typography
                    sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        mt: 1,
                    }}
                >
                    {record.description.substring(0, 100)}
                    {record.description.length > 100 && (
                        <Typography component="span" color="primary">
                            ... קרא עוד
                        </Typography>
                    )}
                </Typography>
            </CardContent>
        </Card>
    );
};
